import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

type AccountType = 'generator' | 'broker' | 'admin';

interface Organization {
  id: string;
  name: string;
  subscriptionTier: string;
  accountType: AccountType;
}

interface AuthState {
  user: User | null;
  organization: Organization | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Computed helpers
  isGenerator: () => boolean;
  isBroker: () => boolean;
  isAdmin: () => boolean;
  isFieldTech: () => boolean;
  canSeeCost: () => boolean;
  getAccountType: () => AccountType;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  accountType?: AccountType;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      organization: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      // Account type helpers
      isGenerator: () => get().organization?.accountType === 'generator',
      isBroker: () => get().organization?.accountType === 'broker',
      isAdmin: () => get().organization?.accountType === 'admin',
      // Field tech = an operator inside an admin/broker org (role-based, not
      // account-type). Drives the field-first dashboard + trimmed nav.
      isFieldTech: () => get().user?.role === 'field_tech',
      // Cost/margin visibility (decision #10): Office and above; Field (user) sees price only.
      // Current enum until the role remap (076) lands -> then swap for office/routing/tenant_admin.
      canSeeCost: () => ['owner', 'admin', 'manager'].includes(get().user?.role ?? ''),
      getAccountType: () => get().organization?.accountType || 'generator',

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, organization, tokens } = response.data.data;

          set({
            user,
            organization,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/register', data);
          const { user, organization, tokens } = response.data.data;

          set({
            user,
            organization,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          organization: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          set({ isLoading: false });
          return;
        }

        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          set({
            accessToken,
            ...(newRefreshToken && { refreshToken: newRefreshToken }),
            isLoading: false,
          });
        } catch (error) {
          // Refresh failed, logout
          get().logout();
        }
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        organization: state.organization,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth state on app load
const initializeAuth = async () => {
  const { accessToken, refreshAuth, setLoading } = useAuthStore.getState();
  if (accessToken) {
    await refreshAuth();
  } else {
    setLoading(false);
  }
};

initializeAuth();
